'use client';

import React, { useRef } from 'react';
import {
  TTypographyAnimationTypes,
  TTypographyTag,
  Typography,
} from '@/components/atoms/Typography/Typography';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { Video } from '@/components/atoms/Video/Video';
import { Image } from '@/components/atoms/Image/Image';
import { ICommonProps, TImageAsset, TVideoAsset } from '@/types/global';
import { getComponenentSeparation } from '@/utils/styling';
import * as Styled from './SingleImageWithText.styled';

export type TEmptySpaceSide = 'left' | 'right';
export type TContentWidth = '3/2' | 'page_side' | 'half';

export interface ISingleImageWithTextProps extends ICommonProps {
  imagePosition: TEmptySpaceSide;
  imgAspectRatio?: '16/9' | '3/2' | '2/1' | '1/1';
  contentWidth: TContentWidth;
  image?: TImageAsset;
  video?: TVideoAsset;
  includeInSlider?: boolean;
  title?: string;
  titleTag?: TTypographyTag;
  highlightText?: string;
  bottomText?: string;
  animationHighlightText: {
    type: TTypographyAnimationTypes;
  };
  textPosition?: 'top' | 'bottom' | 'center';
}
export const SingleImageWithText: React.FC<ISingleImageWithTextProps> = ({
  image,
  imagePosition = 'left',
  marginBottom = 'default',
  includeInSlider = true,
  imgAspectRatio = '2/1',
  contentWidth = '3/2',
  isProject = true,
  video,
  width = 'full',
  title,
  highlightText,
  titleTag = 'h2',
  bottomText,
  textPosition = 'center',
  animationHighlightText = {
    type: 'revealUp',
  },
  animationContainer = {
    type: 'fadeIn',
  },
  marginBottomOnMobile = 'inner',
}) => {
  const imageAspectRatio = (imgAspectRatio ?? '2/1').replace(':', '/');
  const refContainer = useRef<HTMLDivElement>(null);

  // useAnimation({
  //   refContainer: refContainer as React.RefObject<HTMLElement>,
  //   animationType: animationContainer.type,
  //   duration: animationContainer.type === 'fadeIn' ? 0.5 : 2,
  // });

  return (
    <ComponentContainer
      width={width}
      marginBottom={marginBottom}
      isProject={isProject}
      marginBottomOnMobile={marginBottomOnMobile}
      animationContainer={animationContainer}
    >
      <Styled.SingleImageContainer
        imagePosition={imagePosition}
        marginBottom={getComponenentSeparation(marginBottom)}
        className="column"
        contentWidth={contentWidth}
        data-content-width={contentWidth}
        ref={refContainer}
      >
        {(contentWidth === '3/2' || contentWidth === 'half') && (
          <Styled.TextSideContainer textPosition={textPosition}>
            {title && (
              <Typography
                text={title}
                as={titleTag}
                size="xl"
                sizeMobile="2xl"
                lineHeight="lg"
                fontFamily="helvetica-light"
              />
            )}
            {highlightText && (
              <>
                <Styled.PushtToTopContainer />
                <Typography
                  text={highlightText}
                  as={'p'}
                  size="2xl"
                  sizeMobile="4xl"
                  lineHeight="lg"
                  fontFamily="helvetica-light"
                  animation={{
                    type: animationHighlightText.type,
                  }}
                />
              </>
            )}
            <Typography 
              text={bottomText} 
              addIndentAfterLineBreak={false} 
              className="side-text"
            />
          </Styled.TextSideContainer>
        )}
        <Styled.ContentContainer emptySpaceSide={imagePosition}>
          {video && video.vimeoId ? (
            <Video
              type={video.type}
              fill={true}
              {...(video.includesSound
                ? { includesSound: video.includesSound }
                : {})}
              {...(video.vimeoId ? { vimeoId: video.vimeoId } : {})}
              includeContainer={true}
              aspectRatio={imageAspectRatio}
              vimeoMetadata={video.vimeoMetadata}
            />
          ) : (
            image && (
              <Image
                {...image}
                includeContainer={true}
                aspectRatio={imageAspectRatio}
                includeInSlider={includeInSlider}
                sizes={{
                  mobile: '100vw',
                  tablet: '100vw',
                  desktop: '66vw',
                }}
                alt={image.alt}
              />
            )
          )}
          {bottomText && contentWidth === 'page_side' && (
            <div>
              <Typography text={bottomText} addIndentAfterLineBreak={true} />
            </div>
          )}
        </Styled.ContentContainer>
      </Styled.SingleImageContainer>
    </ComponentContainer>
  );
};
